
import { useRef, useState, MouseEvent, useContext } from "react";
import { capitalize } from "../utils";
import { Region } from "../interfaces";
import { PDFDocumentProxy } from "pdfjs-dist";
import SmallTextInputField from "./SmallTextInputField";
import SmallSelectField from "./SmallSelectField";
import { WorkflowContext } from "../contexts";

interface Props {
  pdfDocument: PDFDocumentProxy;
  pageNumber: number;
  regions?: Array<Region>;
  updateRegions?: ((regions: Array<Region>) => void);
  disabled?: boolean;
}

const COLOUR_MAP: Record<string, string> = {
  1: "bg-blue/40",
  2: "bg-purple/40",
  3: "bg-green/40",
  4: "bg-red/40",
}

type Status = "NONE" | "SELECTED" | "MOVING" | "RESIZING";

function PDFViewerOverlay(props: Props) {
  const pageContainerRef = useRef<HTMLDivElement>(null);

  const [currentRegionId, setCurrentRegionId] = useState<string>();
  const [currentOffset, setCurrentOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

  const [activeStatus, setActiveStatus] = useState<Status>("NONE");
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);

  const { workflow } = useContext(WorkflowContext);
  const { signatureConfig } = workflow;

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    if (!currentRegionId || !props.regions || !props.updateRegions)
      return;

    const overlayRect = pageContainerRef.current?.getBoundingClientRect()!;
    const overlayHeight = overlayRect.height;

    const localX = e.clientX - overlayRect?.left;
    const localY = e.clientY - overlayRect?.top;

    const updatedRegions = [...props.regions];

    // TODO: We should store a map for this instead
    const regionIndex = updatedRegions.findIndex(r => r.id === currentRegionId);
    if (regionIndex === -1)
      return;

    const currentRegion = updatedRegions[regionIndex];

    const x = localX - currentOffset.x;
    const y = localY - currentOffset.y;

    const deltaX = Math.abs(currentRegion.rect.x - x);
    const deltaY = Math.abs(currentRegion.rect.y - y)

    if (isMouseDown && (deltaX > 5 || deltaY > 5) && ["NONE", "SELECTED"].includes(activeStatus))
      setActiveStatus("MOVING");

    if (activeStatus === "RESIZING") {
      let width, height;

      if (currentRegion.type === "CHECKBOX") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 20), 400);
        height = width;
      } else if (currentRegion.type === "SIGNATURE") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 120), 800);
        height = width * 0.2;
      } else if (currentRegion.type === "TEXTAREA") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 40), 800);
        height = Math.min(Math.max(localY - currentRegion.rect.y, 40), 800);
      } else if (currentRegion.type === "TEXT") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 40), 800);
        height = 40;
      } else {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 40), 800);
        height = Math.max(width * 0.2, 20);
      }

      updatedRegions[regionIndex] = { ...currentRegion, rect: { ...currentRegion.rect, width, height } };
    }
    else if (activeStatus === "MOVING") {
      if (y < 0 && currentRegion.pageNumber > 1) {
        updatedRegions[regionIndex] = {
          ...currentRegion,
          pageNumber: Math.max(currentRegion.pageNumber - 1, 1),
          rect: {
            ...currentRegion.rect, x, y: overlayHeight - currentRegion.rect.height - 20
          }
        };
        setCurrentRegionId(undefined);

      } else if (y + currentRegion.rect.height > overlayHeight && currentRegion.pageNumber < props.pdfDocument.numPages) {
        updatedRegions[regionIndex] = {
          ...currentRegion,
          pageNumber: Math.min(currentRegion.pageNumber + 1, props.pdfDocument.numPages),
          rect: {
            ...currentRegion.rect, x, y: 20
          }
        };
        setCurrentRegionId(undefined);

      } else {
        updatedRegions[regionIndex] = { ...currentRegion, rect: { ...currentRegion.rect, x, y } };
      }
    }

    props.updateRegions(updatedRegions);
  }

  function handleMouseDown(e: MouseEvent<HTMLDivElement>, regionId: string, opts = { isResizing: false }) {
    if (props.disabled)
      return;

    const { isResizing } = opts;

    const rect = e.currentTarget.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsMouseDown(true);
    if (isResizing)
      setActiveStatus("RESIZING");

    setCurrentOffset({ x, y });
    setCurrentRegionId(regionId);

    e.preventDefault();
    e.stopPropagation();
  }

  function handleMouseUp(e: MouseEvent<HTMLDivElement>) {
    setIsMouseDown(false);

    if (currentRegionId && activeStatus === "NONE")
      setActiveStatus("SELECTED");
    else
      setActiveStatus("NONE");
  }

  function handleDeleteRegion(regionId: string) {
    if (!props.regions || !props.updateRegions)
      return;

    const updatedRegions = props.regions.filter(r => r.id !== regionId);
    props.updateRegions(updatedRegions);
  }

  function handleUpdateRegion(regionId: string, updates: Partial<Region>) {
    if (!currentRegionId || !props.regions || !props.updateRegions)
      return;

    const updatedRegions = [...props.regions];

    const regionIndex = updatedRegions.findIndex(r => r.id === currentRegionId);
    if (regionIndex === -1)
      return;

    const currentRegion = updatedRegions[regionIndex];
    updatedRegions[regionIndex] = { ...currentRegion, ...updates };

    props.updateRegions(updatedRegions);
  }

  return (
    <div className={`absolute left-0 top-0 w-full h-full`} ref={pageContainerRef} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseDown={() => { setCurrentRegionId(undefined); setActiveStatus("NONE") }}>
      {(props?.regions || [])
        .filter(r => r.pageNumber === props.pageNumber)
        .map(region => (
          <div key={region.id} className="absolute" style={{ "left": region.rect.x, "top": region.rect.y }}>
            <div className={`box-border select-none flex items-start justify-between p-2 rounded text-white group cursor-move
              ${activeStatus === "NONE" ? "hover:outline-gray-400 hover:outline-2 hover:outline" : (currentRegionId === region.id ? "outline-blue outline-2 outline z-50" : "")}
              ${COLOUR_MAP[region.recipient]}`}
              style={{ width: region.rect.width, height: region.rect.height }} onMouseDown={(e) => handleMouseDown(e, region.id)}>
              {region.type !== "CHECKBOX" &&
                <>
                  <div className="font-semibold font-gilroy">
                    {region.placeholder || capitalize(region.rect.width < 100 ? (region.type.slice(0, 1) + ".") : region.type)}
                    {region.required &&
                      <span className="text-red">*</span>
                    }
                    {(region.maxLength ?? 0) > 0 &&
                      <span className="text-white text-xs"> [{region.maxLength}]</span>
                    }
                  </div>
                  {region.rect.height >= 32
                    ? <div className="font-gilroy font-semibold w-6 h-6 rounded-full border-2 border-white flex items-center justify-center">{region.recipient}</div>
                    : <div className="font-gilroy font-semibold w-4 h-4 rounded-full border-0 border-white flex items-center justify-center">{region.recipient}</div>
                  }
                </>
              }

              {(region.type === "CHECKBOX" && region.rect.width >= 20) &&
                <div className="flex items-center justify-center w-full h-full">
                  <div className="font-gilroy font-semibold w-4 h-4 -m-2 rounded border-2 border-white flex items-center justify-center text-xs relative overflow-visible">
                    {region.recipient}
                    {region.required &&
                      <span className="text-red absolute -right-1 -top-1.5 text-sm">*</span>
                    }
                  </div>
                </div>
              }
              <div className="absolute -right-2 -top-2 cursor-default" style={{ width: 24, height: region.rect.height + 20 }} />
              {currentRegionId === region.id && (activeStatus === "SELECTED" || activeStatus === "RESIZING") &&
                <>
                  {region.type === "TEXT"
                    ? <div className={`rounded-full bg-gray-400 border-white border-2 -right-2 top-1/2 -translate-y-1/2 absolute w-3 h-6 shadow cursor-ew-resize`} onMouseDown={(e) => handleMouseDown(e, region.id, { isResizing: true })} />
                    : <div className={`rounded-full bg-gray-400 border-white border-2 -right-2 -bottom-2 absolute w-4 h-4 shadow cursor-nwse-resize`} onMouseDown={(e) => handleMouseDown(e, region.id, { isResizing: true })} />

                  }
                </>
              }
            </div>
            {(currentRegionId === region.id && activeStatus === "SELECTED") &&
              <div className="flex flex-col gap-2 bg-white border rounded-sm border-gray-400 p-2 text-black left-0 absolute shadow-lg text-sm z-40 w-64" style={{ top: region.rect.height + 10 }} onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    {capitalize(region.type)}
                  </div>
                  <button className="btn btn-red text-xs px-2 py-1 w-20" onClick={(e) => handleDeleteRegion(region.id)}>
                    Remove
                  </button>
                </div>
                {!!signatureConfig &&
                  <SmallSelectField value={String(region.recipient)} onChange={rec => handleUpdateRegion(region.id, { recipient: Number(rec) })} label="Recipient">
                    {signatureConfig.numRecipients >= 1 &&
                      <option>1 ({signatureConfig.recipientEmail1})</option>
                    }
                    {signatureConfig.numRecipients >= 2 &&
                      <option>2 ({signatureConfig.recipientEmail2})</option>
                    }
                    {signatureConfig.numRecipients >= 3 &&
                      <option>3 ({signatureConfig.recipientEmail3})</option>
                    }
                    {signatureConfig.numRecipients >= 4 &&
                      <option>4 ({signatureConfig.recipientEmail4})</option>
                    }
                  </SmallSelectField>
                }
                {region.type.includes("TEXT") &&
                  <SmallTextInputField value={region.placeholder ?? ""} placeholder={capitalize(region.type)} onChange={placeholder => handleUpdateRegion(region.id, { placeholder })} label="Placeholder" />
                }
                <div className="flex items-center gap-2">
                  {(region.type.includes("TEXT") || region.type === "CHECKBOX") &&
                    <label><input type="checkbox" checked={region.required} onChange={(e) => handleUpdateRegion(region.id, { required: e.currentTarget.checked })} /> Required</label>
                  }
                  {region.type.includes("TEXT") &&
                    <SmallTextInputField value={region.maxLength ? String(region.maxLength) : ""} onChange={len => handleUpdateRegion(region.id, { maxLength: Math.max(0, Number(len)) })} label="Max length" number className="w-1/2 ml-auto" />
                  }
                </div>
              </div>
            }
          </div>
        ))}
    </div>
  )

}

export default PDFViewerOverlay;